import { useState } from "react"
import { router, Link, usePage } from "@inertiajs/react"
import { toast } from "sonner"
import { ArrowLeft, Trash2, Plus } from "lucide-react"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import type { SharedProps } from "@/types/inertia"

// Settings → Apps → New / Edit. Single-page form (no wizard) — three
// stacked sections cover the manifest, the supported placements, and the
// document + optional error_webhook endpoints. Submits straight into
// `Apps::Create` / `Apps::Update` (Track 2) which own all business
// invariants.

type Mode = "new" | "edit"
type Role = "agent" | "agent_manager" | "admin"
type EndpointRole = "document" | "error_webhook"

type EndpointForm = {
  id: number | null
  role: EndpointRole
  httpMethod: string
  url: string
  querySchema: Record<string, unknown> | unknown[] | string
  timeoutMs: number
  cacheTtlSeconds: number
  supportedContextVars: string[]
  description: string
}

type AppForm = {
  id: number | null
  key: string
  name: string
  description: string
  icon: string
  status: "draft" | "private" | "public" | "archived"
  manifestVersion: number
  defaultMinRole: Role
  supportedPlacements: { context: string; location: string }[]
  hasInstalls: boolean
  installCount: number
  endpoints: EndpointForm[]
}

type Props = {
  mode: Mode
  app: AppForm
  isAdmin: boolean
}

const PLACEMENT_CONTEXTS = ["ticket"] as const
const PLACEMENT_LOCATIONS = ["sidebar_tab"] as const

export default function SettingsAppsForm({ mode, app }: Props) {
  const { props } = usePage<SharedProps>()
  const errors = (props as unknown as { errors?: Record<string, string> }).errors ?? {}

  const [key, setKey] = useState(app.key)
  const [name, setName] = useState(app.name)
  const [description, setDescription] = useState(app.description)
  const [icon, setIcon] = useState(app.icon)
  const [defaultMinRole, setDefaultMinRole] = useState<Role>(app.defaultMinRole)
  const [placements, setPlacements] = useState(app.supportedPlacements.length > 0
    ? app.supportedPlacements
    : [{ context: "ticket", location: "sidebar_tab" }])

  const initialDocument = app.endpoints.find((e) => e.role === "document") ?? blankDocumentEndpoint()
  const initialErrorWebhook = app.endpoints.find((e) => e.role === "error_webhook") ?? null

  const [doc, setDoc] = useState<EndpointForm>(initialDocument)
  const [errorWebhook, setErrorWebhook] = useState<EndpointForm | null>(initialErrorWebhook)
  const [errorWebhookEnabled, setErrorWebhookEnabled] = useState<boolean>(initialErrorWebhook != null)

  const [submitting, setSubmitting] = useState(false)

  // `key` is immutable on edit if any install exists. Track 1's App
  // model + Apps::Update both enforce it; we mirror it here so the
  // operator gets immediate feedback rather than a 422 round-trip.
  const keyLocked = mode === "edit" && app.hasInstalls

  function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (submitting) return

    if (placements.length === 0) {
      toast.error("Add at least one supported placement")
      return
    }
    if (!doc.url.trim()) {
      toast.error("Document endpoint needs a url")
      return
    }

    // Validate the JSON editor pre-submit so we get a clean error toast
    // rather than a silent server-side fallback to {}.
    let parsedDocSchema: unknown = doc.querySchema
    if (typeof doc.querySchema === "string") {
      const trimmed = doc.querySchema.trim()
      if (trimmed.length === 0) {
        parsedDocSchema = {}
      } else {
        try {
          parsedDocSchema = JSON.parse(trimmed)
        } catch {
          toast.error("Document endpoint query_schema is not valid JSON")
          return
        }
      }
    }

    let parsedWebhookSchema: unknown = errorWebhook?.querySchema
    if (errorWebhookEnabled && errorWebhook && typeof errorWebhook.querySchema === "string") {
      const trimmed = errorWebhook.querySchema.trim()
      if (trimmed.length === 0) {
        parsedWebhookSchema = {}
      } else {
        try {
          parsedWebhookSchema = JSON.parse(trimmed)
        } catch {
          toast.error("Error webhook query_schema is not valid JSON")
          return
        }
      }
    }

    const endpoints: EndpointForm[] = [
      { ...doc, querySchema: parsedDocSchema as EndpointForm["querySchema"] },
    ]
    if (errorWebhookEnabled && errorWebhook) {
      endpoints.push({ ...errorWebhook, querySchema: parsedWebhookSchema as EndpointForm["querySchema"] })
    }

    const payload = {
      app: {
        key,
        name,
        description,
        icon,
        default_min_role: defaultMinRole,
        manifest_version: app.manifestVersion,
        supported_placements: placements,
        endpoints: endpoints.map((ep) => ({
          role:                   ep.role,
          http_method:            ep.httpMethod,
          url:          ep.url,
          query_schema:           ep.querySchema,
          timeout_ms:             ep.timeoutMs,
          cache_ttl_seconds:      ep.cacheTtlSeconds,
          supported_context_vars: ep.supportedContextVars,
          description:            ep.description,
        })),
      },
    }

    const onSuccess = () => toast.success(mode === "new" ? "App created" : "App saved")
    const onError = (e: Record<string, string>) => {
      const msg = e.base ?? Object.values(e)[0] ?? "Save failed"
      toast.error(msg)
    }

    setSubmitting(true)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const inertiaPayload = payload as any
    if (mode === "new") {
      router.post("/settings/apps", inertiaPayload, {
        onSuccess,
        onError,
        onFinish: () => setSubmitting(false),
      })
    } else {
      router.patch(`/settings/apps/${app.id}`, inertiaPayload, {
        onSuccess,
        onError,
        onFinish: () => setSubmitting(false),
      })
    }
  }

  return (
    <AppShell>
      <PageHeader
        eyebrow={
          <Link href={mode === "new" ? "/settings/apps" : `/settings/apps/${app.id}`} className="inline-flex items-center gap-1 hover:text-app-text">
            <ArrowLeft size={12} strokeWidth={2} /> Back to {mode === "new" ? "Apps" : "App"}
          </Link>
        }
        title={mode === "new" ? "Create App" : `Edit ${app.name || "App"}`}
        subtitle={mode === "new" ? "Define the manifest and document endpoint." : "Manifest changes auto-bump the manifest version on save."}
      />

      {errors.base && (
        <div className="rounded-control border border-status-error/30 bg-status-error/10 px-4 py-2 text-sm text-status-error">
          {errors.base}
        </div>
      )}

      <form onSubmit={submit} className="space-y-5">
        {/* ──────────────── Manifest ──────────────── */}
        <Card className="p-5 sm:p-6">
          <header className="mb-5">
            <h2 className="text-lg font-semibold">Manifest</h2>
            <p className="text-sm text-app-muted">Identity-only metadata. Stored on the global App row.</p>
          </header>

          <div className="space-y-5">
            <Field label="Key" hint={keyLocked
              ? "Immutable — installs already reference this key."
              : "Lowercase, digits and underscores. Used in URLs and SDK identifiers."}>
              <Input
                value={key}
                onChange={(e) => setKey(e.target.value)}
                disabled={keyLocked}
                placeholder="my_app_key"
                required
              />
            </Field>

            <Field label="Name" hint="Shown in install lists and the operator portal.">
              <Input value={name} onChange={(e) => setName(e.target.value)} required />
            </Field>

            <Field label="Description" hint="One- or two-sentence pitch. Plain text.">
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
            </Field>

            <Field label="Icon slug" hint="Optional. Maps to a Lucide icon name or your CDN slug.">
              <Input value={icon} onChange={(e) => setIcon(e.target.value)} placeholder="package" />
            </Field>

            <Field label="Default minimum role" hint="The lowest staff role that can see this App when installed. Tenants may tighten this further per install.">
              <select
                value={defaultMinRole}
                onChange={(e) => setDefaultMinRole(e.target.value as Role)}
                className="appearance-none rounded-control border border-border bg-white/5 py-2 pl-3 pr-9 text-sm text-app-text outline-none focus:border-brand-red/50"
              >
                <option value="agent">agent</option>
                <option value="agent_manager">agent_manager</option>
                <option value="admin">admin</option>
              </select>
            </Field>

            <Field label="Manifest version" hint="Auto-bumps when you change the key, supported placements, or default min_role. Read-only.">
              <Input value={`v${app.manifestVersion}`} disabled />
            </Field>
          </div>
        </Card>

        {/* ──────────────── Placements ──────────────── */}
        <Card className="p-5 sm:p-6">
          <header className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold">Supported placements</h2>
              <p className="text-sm text-app-muted">Where the App can mount. At least one is required.</p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="self-start sm:self-auto"
              onClick={() => setPlacements((p) => [...p, { context: "ticket", location: "sidebar_tab" }])}
            >
              <Plus size={14} strokeWidth={2.5} className="mr-1" /> Add placement
            </Button>
          </header>

          <div className="space-y-3">
            {placements.length === 0 && (
              <div className="rounded-control border border-status-warning/30 bg-status-warning/10 px-3 py-2 text-xs text-status-warning">
                Add at least one placement before saving.
              </div>
            )}
            {placements.map((p, idx) => (
              <div key={idx} className="flex flex-wrap items-center gap-2 sm:gap-3">
                <select
                  value={p.context}
                  onChange={(e) => setPlacements((arr) => arr.map((q, i) => i === idx ? { ...q, context: e.target.value } : q))}
                  className="appearance-none rounded-control border border-border bg-white/5 py-2 pl-3 pr-9 text-sm text-app-text outline-none focus:border-brand-red/50"
                >
                  {PLACEMENT_CONTEXTS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <span className="text-app-subtle">.</span>
                <select
                  value={p.location}
                  onChange={(e) => setPlacements((arr) => arr.map((q, i) => i === idx ? { ...q, location: e.target.value } : q))}
                  className="appearance-none rounded-control border border-border bg-white/5 py-2 pl-3 pr-9 text-sm text-app-text outline-none focus:border-brand-red/50"
                >
                  {PLACEMENT_LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
                <button
                  type="button"
                  onClick={() => setPlacements((arr) => arr.filter((_, i) => i !== idx))}
                  className="ml-auto inline-flex h-9 w-9 items-center justify-center rounded-control text-app-subtle hover:bg-white/5 hover:text-status-error"
                  aria-label="Remove placement"
                >
                  <Trash2 size={14} strokeWidth={2} />
                </button>
              </div>
            ))}
          </div>
        </Card>

        {/* ──────────────── Document endpoint ──────────────── */}
        <Card className="p-5 sm:p-6">
          <header className="mb-5">
            <h2 className="text-lg font-semibold">Document endpoint</h2>
            <p className="text-sm text-app-muted">
              GET-only. Returns the structured UI document Valet renders. Required.
            </p>
          </header>
          <EndpointFields
            endpoint={doc}
            onChange={setDoc}
            roleLocked
            httpMethodLocked="GET"
          />
        </Card>

        {/* ──────────────── Error webhook (optional) ──────────────── */}
        <Card className="p-5 sm:p-6">
          <header className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold">Error webhook <span className="text-xs font-normal text-app-muted">(optional)</span></h2>
              <p className="text-sm text-app-muted">
                POST-only. Receives validation/fetch failure payloads when Valet rejects a document.
              </p>
            </div>
            <label className="inline-flex items-center gap-2 self-start text-sm text-app-text sm:self-auto">
              <input
                type="checkbox"
                checked={errorWebhookEnabled}
                onChange={(e) => {
                  setErrorWebhookEnabled(e.target.checked)
                  if (e.target.checked && errorWebhook == null) {
                    setErrorWebhook(blankErrorWebhook())
                  }
                }}
                className="h-4 w-4 accent-brand-red"
              />
              Configure
            </label>
          </header>

          {errorWebhookEnabled && errorWebhook && (
            <EndpointFields
              endpoint={errorWebhook}
              onChange={setErrorWebhook}
              roleLocked
              httpMethodLocked="POST"
            />
          )}
        </Card>

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Link href={mode === "new" ? "/settings/apps" : `/settings/apps/${app.id}`} className="sm:contents">
            <Button type="button" variant="ghost" className="w-full sm:w-auto">Cancel</Button>
          </Link>
          <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
            {submitting ? "Saving…" : (mode === "new" ? "Create App" : "Save changes")}
          </Button>
        </div>
      </form>
    </AppShell>
  )
}

function EndpointFields({
  endpoint, onChange, httpMethodLocked,
}: {
  endpoint: EndpointForm
  onChange: (next: EndpointForm) => void
  roleLocked: boolean
  httpMethodLocked: string
}) {
  function patch(p: Partial<EndpointForm>) { onChange({ ...endpoint, ...p }) }

  // Normalize the JSON editor value to a string so the textarea has a
  // single source of truth. We re-parse on submit.
  const querySchemaString = typeof endpoint.querySchema === "string"
    ? endpoint.querySchema
    : JSON.stringify(endpoint.querySchema ?? {}, null, 2)

  return (
    <div className="space-y-5">
      <Field label="HTTP method" hint={`Locked to ${httpMethodLocked} for this endpoint role.`}>
        <Input value={endpoint.httpMethod || httpMethodLocked} disabled />
      </Field>

      <Field label="Path template" hint="Use {source_key}, {convo_uuid}, {appuser_id} for context vars. Must appear in supported_context_vars below.">
        <Input
          value={endpoint.url}
          onChange={(e) => patch({ url: e.target.value })}
          placeholder="/api/v1/apps/my_app/document?ticket={convo_uuid}"
          required
        />
      </Field>

      <Field label="Supported context vars" hint="Comma-separated. Only vars listed here are allowed in url.">
        <Input
          value={endpoint.supportedContextVars.join(", ")}
          onChange={(e) => patch({
            supportedContextVars: e.target.value.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean),
          })}
          placeholder="source_key, convo_uuid, appuser_id"
        />
      </Field>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Cache TTL (seconds)" hint="0 disables caching.">
          <Input
            type="number"
            min={0}
            value={endpoint.cacheTtlSeconds}
            onChange={(e) => patch({ cacheTtlSeconds: Number(e.target.value || 0) })}
          />
        </Field>
        <Field label="Timeout (ms)" hint="Total request timeout. 8000 is a sensible default.">
          <Input
            type="number"
            min={1}
            value={endpoint.timeoutMs}
            onChange={(e) => patch({ timeoutMs: Number(e.target.value || 0) })}
            required
          />
        </Field>
      </div>

      <Field label="Query schema (JSON)" hint="JSON Schema describing the query string. Leave as {} if you don't validate.">
        <textarea
          value={querySchemaString}
          onChange={(e) => patch({ querySchema: e.target.value })}
          rows={6}
          className="w-full rounded-control border border-border bg-white/5 px-3 py-2 font-mono text-xs text-app-text outline-none focus:border-brand-red/50"
        />
      </Field>

      <Field label="Description" hint="Optional. Free-form note for your future self.">
        <Textarea
          value={endpoint.description}
          onChange={(e) => patch({ description: e.target.value })}
          rows={2}
        />
      </Field>
    </div>
  )
}

function Field({ label, hint, children }: { label: React.ReactNode; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mb-1 text-sm font-medium text-app-text">{label}</div>
      {hint && <div className="mb-2 text-xs text-app-muted">{hint}</div>}
      {children}
    </label>
  )
}

function blankDocumentEndpoint(): EndpointForm {
  return {
    id: null,
    role: "document",
    httpMethod: "GET",
    url: "",
    querySchema: {},
    timeoutMs: 8000,
    cacheTtlSeconds: 60,
    supportedContextVars: ["source_key", "convo_uuid"],
    description: "",
  }
}

function blankErrorWebhook(): EndpointForm {
  return {
    id: null,
    role: "error_webhook",
    httpMethod: "POST",
    url: "",
    querySchema: {},
    timeoutMs: 8000,
    cacheTtlSeconds: 0,
    supportedContextVars: [],
    description: "",
  }
}
